import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { catchError, filter, map, of, switchMap, take, timeout } from 'rxjs';
import { ContextService } from '../services/context/context.service';
import { ContextStoreService } from '../services/context/context-store.service';
import { PermissionsService } from '../services/permissions/permissions.service';
import { deniedMessage, ruleForUrl } from '../services/permissions/route-permissions';
import { restoreContextFromStorage } from './restore-context';

/** Attente max du rechargement de la matrice avant de statuer avec l'ancienne. */
const MATRIX_WAIT_TIMEOUT_MS = 2500;
/** Attente max du contexte (base active) avant de laisser passer. */
const CONTEXT_WAIT_TIMEOUT_MS = 5000;

/**
 * Dernier toast affiché : deux refus peuvent s'enchaîner (page d'import →
 * redirection vers /dashboard, refusé aussi) — un seul message suffit.
 */
let lastToast = { detail: '', at: 0 };

/**
 * Guard global des pages privées (canActivateChild du layout) :
 *
 * 1. garantit une matrice des droits fraîche (< 15 s) avant chaque
 *    navigation — un superadmin a pu la modifier depuis le login, et les
 *    pages calculent leurs droits à l'initialisation ;
 * 2. refuse les pages inutilisables sans leur droit (ROUTE_PERMISSION_RULES),
 *    avec un toast explicatif et une redirection.
 *
 * En cas de doute (contexte introuvable, réseau), on laisse passer : le back
 * reste le garde-fou et refuse les appels.
 */
export const permissionsGuard: CanActivateChildFn = (_route, state) => {
  const router = inject(Router);
  const messages = inject(MessageService);
  const contextService = inject(ContextService);
  const contextStore = inject(ContextStoreService);
  const permissions = inject(PermissionsService);

  // Non connecté : AuthGuard (sur l'enfant, exécuté APRÈS ce guard) redirige
  // vers le login — ne pas le retarder en attendant contexte et matrice.
  if (!localStorage.getItem('access_token')) {
    return true;
  }

  const rule = ruleForUrl(state.url);

  // Utilisateur (et contexte, si la page est protégée) connus AVANT de
  // demander la matrice : sans utilisateur, loadMatrix() ne charge rien.
  // Le chargement lancé par l'init est partagé, ensureFreshMatrix l'attend.
  if (rule) {
    restoreContextFromStorage(contextStore, contextService, permissions);
  } else if (!permissions.currentUser) {
    permissions.initUserFromStorage();
  }

  const fresh$ = permissions.ensureFreshMatrix().pipe(
    timeout(MATRIX_WAIT_TIMEOUT_MS),
    catchError(() => of(undefined)),
  );

  if (!rule) {
    return fresh$.pipe(map(() => true));
  }

  return fresh$.pipe(
    switchMap(() => contextService.context$.pipe(
      filter((context) => !!context?.base),
      take(1),
      timeout(CONTEXT_WAIT_TIMEOUT_MS),
    )),
    map((context) => {
      if (permissions.canAny(rule.anyOf, context)) return true;
      const detail = deniedMessage(rule);
      if (Date.now() - lastToast.at > 1500) {
        messages.add({ key: 'tst', severity: 'warn', summary: 'Accès restreint', detail, life: 5000 });
        lastToast = { detail, at: Date.now() };
      }
      return router.parseUrl(rule.redirect ?? '/chat');
    }),
    catchError(() => of(true)),
  );
};
